/**
 * Trash and the purge (FR-DEL-01 to FR-DEL-05).
 *
 * Pure. Deciding *what may be destroyed* is the highest-stakes calculation in the app, so
 * it lives here with tests rather than inside the scheduled job that acts on it.
 *
 * The rule is thirty days from deletion, and two things follow from it that are easy to
 * get wrong:
 *
 *  · **Purging is by `purgeAfter`, not by "thirty days before today".** The date is
 *    stamped at deletion. Recomputing it at purge time would move the deadline whenever
 *    the retention period was changed, and would silently destroy records that had been
 *    deleted under a longer one.
 *  · **The audit log is never purged** (FR-DEL-03). The record goes; the record of what
 *    happened to it stays. That is the whole point of keeping entries keyed by collection
 *    and id as plain strings rather than as references.
 */

import { addDays, type DateKey } from './dates';

export const TRASH_DAYS = 30;

export interface Trashed {
  id: string;
  collection: string;
  label: string;
  deletedOn: DateKey;
  /** Stamped at deletion. The purge reads this and never recomputes it. */
  purgeAfter: DateKey;
  /** Storage paths that go with the record (FR-DEL-02). */
  attachments: string[];
  amountPaise?: number;
}

/** Days left before a record is destroyed. Negative means it is already due. */
export function daysRemaining(item: Pick<Trashed, 'purgeAfter'>, today: DateKey): number {
  const from = Date.UTC(
    Number(today.slice(0, 4)), Number(today.slice(5, 7)) - 1, Number(today.slice(8, 10)),
  );
  const to = Date.UTC(
    Number(item.purgeAfter.slice(0, 4)),
    Number(item.purgeAfter.slice(5, 7)) - 1,
    Number(item.purgeAfter.slice(8, 10)),
  );
  return Math.round((to - from) / 86_400_000);
}

export function purgeAfterFor(deletedOn: DateKey, days = TRASH_DAYS): DateKey {
  return addDays(deletedOn, days);
}

/**
 * What the purge job may destroy today.
 *
 * A record with no `purgeAfter` is **kept**, not purged. That default matters: a soft
 * delete written by an older build, or one whose stamp failed, would otherwise be treated
 * as infinitely old and destroyed on the next run.
 */
export function duePurge(items: Trashed[], today: DateKey): Trashed[] {
  return items.filter((item) => Boolean(item.purgeAfter) && item.purgeAfter <= today);
}

export interface PurgePlan {
  records: Trashed[];
  /** Every attachment path that goes with them (FR-DEL-02 — no orphaned files). */
  attachments: string[];
  /** Stated so the job's log can say it, and so a reader can check it is true. */
  auditEntriesRemoved: 0;
}

export function planPurge(items: Trashed[], today: DateKey): PurgePlan {
  const records = duePurge(items, today);
  return {
    records,
    attachments: records.flatMap((r) => r.attachments),
    // FR-DEL-03, as a value rather than as a comment: the purge removes no audit entries.
    auditEntriesRemoved: 0,
  };
}

export interface TrashSummary {
  total: number;
  dueToday: number;
  /** Soonest first — what is about to be lost is what the screen should lead with. */
  soonest: Trashed[];
  attachments: number;
}

export function summarise(items: Trashed[], today: DateKey): TrashSummary {
  const sorted = [...items].sort(
    (a, b) => a.purgeAfter.localeCompare(b.purgeAfter) || a.label.localeCompare(b.label),
  );

  return {
    total: items.length,
    dueToday: duePurge(items, today).length,
    soonest: sorted.slice(0, 5),
    attachments: items.reduce((n, item) => n + item.attachments.length, 0),
  };
}

/**
 * Wording for the trash row.
 *
 * "3 days left" reads as urgency in a way "purges on 2026-09-04" does not, and urgency is
 * the useful signal on a screen whose whole job is a last chance.
 */
export function describeRemaining(item: Trashed, today: DateKey): string {
  const days = daysRemaining(item, today);
  if (days < 0) return 'due to be purged';
  if (days === 0) return 'purges today';
  if (days === 1) return '1 day left';
  return `${days} days left`;
}
